import { z } from "zod";
import { GroupReference } from "./analytics";
const literalSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null()
]);
const JSONValue = z.lazy(()=>z.union([
        literalSchema,
        z.array(JSONValue),
        z.record(JSONValue)
    ]));
const JSONObject = z.record(JSONValue);
const EventProperties = z.record(z.any());
const Integrations = z.record(z.union([
    z.boolean(),
    JSONObject
]));
const CoreExtraContext = z.object({
    active: z.boolean().optional(),
    ip: z.string().optional(),
    locale: z.string().optional(),
    location: z.object({
        city: z.string().optional(),
        country: z.string().optional(),
        latitude: z.string().optional(),
        longitude: z.string().optional(),
        region: z.string().optional(),
        speed: z.number().optional()
    }).optional(),
    page: z.object({
        path: z.string().optional(),
        referrer: z.string().optional(),
        search: z.string().optional(),
        title: z.string().optional(),
        url: z.string().optional()
    }).optional(),
    userAgent: z.string().optional(),
    library: z.object({
        name: z.string(),
        version: z.string()
    }).optional(),
    traits: z.record(z.any()).optional(),
    campaign: z.object({
        name: z.string(),
        term: z.string(),
        source: z.string(),
        medium: z.string(),
        content: z.string()
    }).partial().optional(),
    referrer: z.object({
        type: z.string().optional(),
        name: z.string().optional(),
        url: z.string().optional(),
        link: z.string().optional(),
        id: z.string().optional(),
        btid: z.string().optional(),
        urid: z.string().optional()
    }).partial().optional(),
    // This is a special field that we use to resolve the groupId for
    // the group event
    groupReference: GroupReference.optional(),
    amp: z.object({
        id: z.string()
    }).optional()
}).passthrough();
const SegmentEventType = z.enum([
    "track",
    "page",
    "identify",
    "group",
    "alias",
    "screen"
]);
const CoreOptions = z.object({
    integrations: Integrations.optional(),
    timestamp: z.union([
        z.string(),
        z.date()
    ]).optional(),
    context: CoreExtraContext.optional(),
    anonymousId: z.string().optional(),
    userId: z.string().optional(),
    traits: z.record(z.any()).optional()
}).passthrough();
const SegmentEventMetadata = z.object({
    failedInitializations: z.array(z.any()).optional(),
    bundled: z.array(z.string()).optional(),
    unbundled: z.array(z.string()).optional(),
    nodeVersion: z.string().optional(),
    bundledConfigIds: z.array(z.string()).optional(),
    unbundledConfigIds: z.array(z.string()).optional(),
    bundledIds: z.array(z.string()).optional()
});
const CoreSegmentEvent = z.object({
    messageId: z.string().optional(),
    type: SegmentEventType,
    category: z.string().optional(),
    name: z.string().optional(),
    properties: EventProperties.optional(),
    traits: z.record(z.any()).optional(),
    integrations: Integrations.optional(),
    context: CoreExtraContext.optional(),
    options: CoreOptions.optional(),
    userId: z.string().optional().nullable(),
    anonymousId: z.string().optional().nullable(),
    groupId: z.string().optional(),
    previousId: z.string().optional(),
    event: z.string().optional(),
    writeKey: z.string().optional(),
    sentAt: z.string().optional(),
    _metadata: SegmentEventMetadata.optional(),
    timestamp: z.union([
        z.string(),
        z.date()
    ]).optional()
});
const PlanEvent = z.object({
    enabled: z.boolean(),
    integrations: z.record(z.boolean()).optional()
});
const TrackPlan = z.record(PlanEvent);
const Plan = z.object({
    track: TrackPlan.optional(),
    identify: TrackPlan.optional(),
    group: TrackPlan.optional()
});
const Address = z.object({
    city: z.string().optional(),
    country: z.string().optional(),
    postalCode: z.string().optional(),
    state: z.string().optional(),
    street: z.string().optional()
});
const BaseGroupTraits = z.object({
    address: Address.optional(),
    avatar: z.string().optional(),
    createdAt: z.union([
        z.string(),
        z.date()
    ]).optional(),
    description: z.string().optional(),
    email: z.string().optional(),
    employees: z.union([
        z.string(),
        z.number()
    ]).optional(),
    id: z.union([
        z.string(),
        z.number()
    ]).optional(),
    industry: z.string().optional(),
    name: z.string().optional(),
    phone: z.union([
        z.string(),
        z.number()
    ]).optional(),
    website: z.string().optional(),
    plan: z.string().optional()
});
const BaseUserTraits = z.object({
    id: z.union([
        z.string(),
        z.number()
    ]).optional(),
    industry: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    name: z.string().optional(),
    phone: z.union([
        z.string(),
        z.number()
    ]).optional(),
    title: z.string().optional(),
    username: z.string().optional(),
    website: z.string().optional(),
    address: Address.optional(),
    age: z.number().optional(),
    avatar: z.string().optional(),
    birthday: z.union([
        z.string(),
        z.date()
    ]).optional(),
    company: z.object({
        name: z.string().optional(),
        id: z.union([
            z.string(),
            z.number()
        ]).optional(),
        industry: z.string().optional(),
        employee_count: z.number().optional(),
        plan: z.string().optional()
    }).optional(),
    plan: z.string().optional(),
    createdAt: z.union([
        z.string(),
        z.date()
    ]).optional(),
    description: z.string().optional(),
    email: z.string().optional(),
    gender: z.string().optional()
});
const GroupTraits = z.intersection(BaseGroupTraits, z.record(z.any()));
const UserTraits = z.intersection(BaseUserTraits, z.record(z.any()));
const Traits = z.union([
    UserTraits,
    GroupTraits
]);
// Export the schemas
export { // EventProperties,
// Integrations,
// CoreExtraContext,
// SegmentEventType,
// CoreOptions,
// SegmentEventMetadata,
CoreSegmentEvent,  };
